"use client";

import TiltCard from "@/components/ui/TiltCard";
import { Mail } from "lucide-react";
import Image from "next/image";
import { FaLinkedin } from "react-icons/fa6";

interface FounderCardProps {
    name: string;
    role: string;
    bio: string;
    imageSrc: string;
    email: string;
    linkedin: string;
    education?: string;
    previousWork?: string;
    expertise?: string[];
    metric?: string;
}

export default function FounderCard({
    name,
    role,
    bio,
    imageSrc,
    email,
    linkedin,
    education,
    previousWork,
    expertise,
    metric
}: FounderCardProps) {
    return (
        <TiltCard
            className="bg-gray-50 p-8 md:p-10 rounded-2xl border border-gray-100 text-center md:text-left h-full group"
            innerClassName="h-full flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-10"
        >
            <div className="flex flex-col items-center shrink-0">
                <div className="w-48 h-48 rounded-full overflow-hidden bg-gray-200 relative hover:scale-105 transition-transform duration-500 border-4 border-white shadow-lg mb-6">
                    <Image
                        src={imageSrc}
                        alt={name}
                        fill
                        className="object-cover"
                    />
                </div>
                
                <div className="flex items-center gap-4">
                    <a
                        href={`mailto:${email}`}
                        className="p-2.5 bg-white rounded-full text-gray-600 hover:text-primary hover:shadow-md transition-all border border-gray-100"
                    >
                        <Mail size={18} />
                    </a>
                    <a
                        href={linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 bg-white rounded-full text-gray-600 hover:text-[#0077b5] hover:shadow-md transition-all border border-gray-100"
                    >
                        <FaLinkedin size={18} />
                    </a>
                </div>
            </div>

            <div className="flex-1 flex flex-col items-center md:items-start h-full">
                <h4 className="text-3xl font-bold mb-1 text-gray-900">{name}</h4>
                <p className="text-sm font-bold text-primary uppercase tracking-wider mb-2">{role}</p>

                {(education || previousWork) && (
                    <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2 mb-4 text-xs text-gray-500 font-medium">
                        {education && <span>{education}</span>}
                        {education && previousWork && <span className="hidden md:inline text-gray-300">|</span>}
                        {previousWork && <span className="text-gray-400">({previousWork})</span>}
                    </div>
                )}

                <p className="text-gray-600 leading-relaxed text-sm md:text-base mb-6 text-center md:text-left">
                    {bio}
                </p>

                <div className="w-full flex flex-col sm:flex-row items-center md:items-start gap-6 mt-auto">
                    {metric && (
                        <div className="px-4 py-2 bg-gray-100/50 rounded-lg border border-gray-200/50 shrink-0 text-center md:text-left">
                            <span className="text-xl font-bold text-gray-900 block">{metric}</span>
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Key Impact</span>
                        </div>
                    )}

                    {expertise && expertise.length > 0 && (
                        <div className="flex flex-wrap justify-center md:justify-start gap-2">
                            {expertise.map((tag, i) => (
                                <span key={i} className="px-2.5 py-1 bg-white border border-gray-200 rounded-md text-[10px] font-bold text-gray-600 uppercase tracking-wide">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </TiltCard>
    );
}
